import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useI18n } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import { Search, ChevronLeft, ChevronRight, Upload, Plus, X, Download, Loader, UserPlus } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('nb-NO', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function LeadBadge({ score }) {
  if (score == null) return <span className="text-slate-500 text-xs">-</span>;
  const cls =
    score >= 70 ? 'bg-green-500/20 text-green-400' :
    score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400';
  return (
    <span className={`text-xs font-mono font-medium px-2 py-0.5 rounded ${cls}`}>
      {score}
    </span>
  );
}

// ─── CSV Import Modal ────────────────────────────────────────────────────────

function CSVImportModal({ onClose, onSuccess }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.csv')) setFile(f);
    else setError('Kun CSV-filer er støttet');
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/contacts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Import feilet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-navy-900 border border-navy-600 rounded-2xl p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-bold text-white">Importer CSV</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {!result ? (
          <>
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-4 ${
                dragging ? 'border-accent-blue bg-accent-blue/5' : 'border-navy-600 hover:border-slate-500'
              }`}
            >
              <Upload size={28} className="mx-auto mb-2 text-slate-400" />
              {file ? (
                <p className="text-sm text-white font-medium">{file.name}</p>
              ) : (
                <>
                  <p className="text-sm text-slate-300">Dra og slipp CSV-fil her</p>
                  <p className="text-xs text-slate-500 mt-1">eller klikk for å velge fil</p>
                </>
              )}
              <input ref={inputRef} type="file" accept=".csv" onChange={handleFile} className="hidden" />
            </div>

            {/* Expected format */}
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-500 mb-1.5 font-semibold uppercase tracking-wider">Forventet format</p>
              <code className="text-xs text-slate-300 font-mono">phone_number,name,email,tags,notes</code>
              <br />
              <code className="text-xs text-slate-500 font-mono">+4791234567,Ola Nordmann,ola@example.com,vip,Notater her</code>
            </div>

            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 border border-navy-600 text-slate-300 hover:bg-navy-800 px-4 py-2 rounded-lg text-sm transition-colors">
                Avbryt
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || loading}
                className="flex-1 bg-accent-blue hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Importerer...' : 'Importer'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload size={20} className="text-green-400" />
            </div>
            <p className="text-white font-semibold mb-2">Import fullført</p>
            <div className="flex justify-center gap-4 text-sm mb-6">
              <span className="text-green-400">{result.imported ?? 0} importert</span>
              <span className="text-yellow-400">{result.updated ?? 0} oppdatert</span>
              <span className="text-red-400">{result.errors ?? 0} feil</span>
            </div>
            <button onClick={onClose} className="bg-accent-blue hover:bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
              Lukk
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Contact Modal ───────────────────────────────────────────────────────

function AddContactModal({ onClose, onSuccess }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Telefonnummer er påkrevd'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/contacts', { phone_number: phone.trim(), name: name.trim() });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-navy-900 border border-navy-600 rounded-2xl p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-bold text-white">Legg til kontakt</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Telefonnummer *</label>
            <input
              type="tel"
              placeholder="+4791234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-navy-800 border border-navy-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Navn</label>
            <input
              type="text"
              placeholder="Ola Nordmann"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-navy-800 border border-navy-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-navy-600 text-slate-300 hover:bg-navy-800 px-4 py-2 rounded-lg text-sm transition-colors">
              Avbryt
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-accent-blue hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Lagrer...' : 'Legg til'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const TABS = [
  { key: 'alle', label: 'Alle' },
  { key: 'kunder', label: 'Eksisterende kunder' },
  { key: 'leads', label: 'Varme leads' },
  { key: 'nyeste', label: 'Nyeste' },
];

export default function Contacts() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'alle');
  const [loading, setLoading] = useState(true);
  const [showCSV, setShowCSV] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchInputRef = useRef(null);

  // Keyboard shortcut: '/' focuses search.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (tab === 'kunder') params.set('filter', 'existing');
      if (tab === 'leads') params.set('filter', 'warm_leads');
      if (tab === 'nyeste') params.set('sort', 'newest');
      const { data } = await api.get(`/api/contacts?${params}`);
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke laste kontakter');
    } finally {
      setLoading(false);
    }
  }, [page, search, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // Sync filters to URL for shareability
  useEffect(() => {
    const params = {};
    if (search) params.q = search;
    if (tab && tab !== 'alle') params.tab = tab;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [search, tab, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleTabChange = (key) => {
    setTab(key);
    setPage(1);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (tab === 'kunder') params.set('filter', 'existing');
      if (tab === 'leads') params.set('filter', 'warm_leads');
      const response = await api.get(`/api/contacts/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `contacts-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
      toast.error(t('contacts_export_error'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('contacts_title')}</h1>
          <p className="text-slate-500 text-sm mt-1">{total} kontakter totalt</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            data-testid="contacts-export-csv"
            className="border border-navy-600 text-slate-300 hover:bg-navy-800 px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
            title="Last ned alle kontakter som CSV"
          >
            {exporting ? <Loader size={15} className="animate-spin" /> : <Download size={15} />}
            {exporting ? t('contacts_exporting') : t('contacts_export')}
          </button>
          <button
            onClick={() => setShowCSV(true)}
            className="border border-navy-600 text-slate-300 hover:bg-navy-800 px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <Upload size={15} />
            {t('contacts_import')}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-accent-blue hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Plus size={15} />
            {t('contacts_add')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('contacts_search') + '  ( / )'}
          value={search}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
        />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 border-b border-navy-600">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-accent-blue text-white'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Mobile cards (< md) */}
      <div className="md:hidden space-y-3" data-testid="contacts-mobile-list">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-navy-800 border border-navy-600 rounded-lg p-4 animate-pulse space-y-2">
              <div className="h-4 bg-navy-700 rounded w-2/3" />
              <div className="h-3 bg-navy-700 rounded w-1/3" />
            </div>
          ))
        ) : contacts.length === 0 ? (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-8 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/10 flex items-center justify-center mb-3">
              <UserPlus size={20} className="text-accent-blue" />
            </div>
            <p className="text-sm font-medium text-white mb-1">{t('contacts_no_results')}</p>
            <p className="text-xs text-slate-500">{search ? `Ingen treff på “${search}”` : 'Ingen kontakter ennå.'}</p>
          </div>
        ) : contacts.map((c) => (
          <div
            key={c._id}
            onClick={() => navigate(`/contacts/${c._id}`)}
            className="bg-navy-800 border border-navy-600 rounded-lg p-4 active:bg-navy-700 transition-colors"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{c.name || 'Ukjent'}</div>
                <div className="text-xs text-slate-400 font-mono truncate">{c.phone_number}</div>
              </div>
              <LeadBadge score={c.avg_lead_score} />
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-slate-500 flex-wrap">
              <span>{c.call_count ?? 0} samtaler</span>
              <span>•</span>
              <span>{formatDate(c.last_call_at)}</span>
              {c.last_service_requested && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-navy-700 text-slate-300 ml-auto">{c.last_service_requested}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Table (md+) */}
      <div className="hidden md:block bg-navy-800 border border-navy-600 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Navn</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Samtaler</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Siste samtale</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Siste tjeneste</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Lead score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Tags</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="animate-pulse border-b border-navy-700">
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-navy-700 rounded w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center" data-testid="contacts-empty-state">
                      <div className="w-14 h-14 rounded-full bg-accent-blue/10 flex items-center justify-center mb-4">
                        <UserPlus size={22} className="text-accent-blue" />
                      </div>
                      <p className="text-sm font-medium text-white mb-1">{t('contacts_no_results')}</p>
                      <p className="text-xs text-slate-500 max-w-sm leading-relaxed">
                        {search
                          ? `Ingen kontakter matcher “${search}”.`
                          : 'Innringere lagres automatisk som kontakter etter første samtale. Du kan også importere en CSV eller legge til manuelt.'}
                      </p>
                      {!search && (
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => setShowAdd(true)}
                            className="text-xs px-3 py-1.5 bg-accent-blue hover:bg-blue-600 text-white rounded-md transition-colors"
                          >
                            Legg til kontakt
                          </button>
                          <button
                            onClick={() => setShowCSV(true)}
                            className="text-xs px-3 py-1.5 bg-navy-700 hover:bg-navy-600 text-white rounded-md transition-colors"
                          >
                            Importer CSV
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr
                    key={c._id}
                    onClick={() => navigate(`/contacts/${c._id}`)}
                    className="hover:bg-navy-700 cursor-pointer transition-colors border-b border-navy-700 last:border-0"
                  >
                    <td className="px-4 py-3.5">
                      <span className="text-sm font-medium text-white">{c.name || 'Ukjent'}</span>
                      {c.email && <span className="block text-xs text-slate-500">{c.email}</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-400 font-mono">{c.phone_number}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-300">{c.call_count ?? 0}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-400">{formatDate(c.last_call_at)}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs px-2 py-1 rounded bg-navy-700 text-slate-300">
                        {c.last_service_requested || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <LeadBadge score={c.avg_lead_score} />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-navy-600 text-slate-300">
                            {tag}
                          </span>
                        ))}
                        {(c.tags || []).length > 3 && (
                          <span className="text-xs text-slate-500">+{c.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-navy-600">
            <span className="text-xs text-slate-500">Side {page} av {pages}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white hover:border-accent-blue disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white hover:border-accent-blue disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showCSV && (
        <CSVImportModal
          onClose={() => setShowCSV(false)}
          onSuccess={() => { fetchContacts(); }}
        />
      )}
      {showAdd && (
        <AddContactModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => { fetchContacts(); }}
        />
      )}
    </div>
  );
}
